// ─────────────────────────────────────────────────────────────────────────────
// okrPlansCoachStore — the coach's OKR Plans data (BE_HANDOFF.md §2.7–2.9,
// OKR_PLANS_COACH.md §8). Same architecture as the coach `quarterlyOkrStore`:
// a vanilla zustand store, a hand-rolled hook (React 17), REST calls here,
// the coach Bearer token (`resolveAuthHeader`).
//
//   • every call re-throws — the screen owns the message (`getErrorMessage`,
//     branch on `getErrorCode`, BE §2.0);
//   • reads keep the payload whole, keyed by id; `pending[key]` tracks
//     in-flight calls.
// ─────────────────────────────────────────────────────────────────────────────

import axios from "axios";
import { createStore } from "zustand/vanilla";
import { useEffect, useState } from "react";
import { getCookie } from "../../../../utils/cookieUtils";
import {
  ACCESS_TOKEN_COOKIE_KEY,
  ADMIN_ACCESS_TOKEN_COOKIE_KEY,
  ADMINS_COACH_ID_COOKIE_KEY,
} from "../../../../data/enums/TokenEnums";
import { OKR_PLANS_COACH_URLS as URLS } from "./okrPlansCoachUrls";

const nodeInstance = axios.create({
  baseURL: process.env.REACT_APP_NEST_BASE_URL,
});

const resolveAuthHeader = () => {
  const accessToken = getCookie(ACCESS_TOKEN_COOKIE_KEY);
  const adminAccessToken = getCookie(ADMIN_ACCESS_TOKEN_COOKIE_KEY);
  const adminsCoachId = getCookie(ADMINS_COACH_ID_COOKIE_KEY);
  if (accessToken) return `Bearer ${accessToken}`;
  if (adminAccessToken && adminsCoachId) return `Bearer ${adminAccessToken}`;
  return null;
};

export const getErrorCode = (err) =>
  err?.response?.data?.code || err?.response?.data?.error || null;
export const getErrorMessage = (err) =>
  err?.response?.data?.message ||
  "Something went wrong. Please try again in a moment.";

// Payload, or the `data` inside a `{ message, data }` wrapper.
const call = (method, url, body) => {
  const authHeader = resolveAuthHeader();
  const config = { headers: authHeader ? { Authorization: authHeader } : {} };
  const req =
    method === "get" || method === "delete"
      ? nodeInstance[method](url, config)
      : nodeInstance[method](url, body ?? {}, config);
  return req.then(({ data: payload } = {}) =>
    payload && typeof payload === "object" && "data" in payload
      ? payload.data
      : (payload ?? null),
  );
};

const INITIAL_STATE = {
  companies: null, // BE 2.7 companies
  quarters: {}, // { [companyId]: quarters[] }
  members: {}, // { [`${companyId}:${quarterCompanyId}`]: members payload }
  plans: {}, // { [planId]: plan detail }
  changeLogs: {}, // { [planId]: { items, next_before } }
  reports: {}, // { [reportId]: report }
  reviews: {}, // { [planId]: final-review panel }
  chats: {}, // { [chatId]: { chat, messages } } owner's chats, read-only
  pending: {},
  // The company the coach came from (breadcrumbs on plan screens).
  lastCompany: null, // { id, name, quarterCompanyId }
};

const okrPlansCoachStore = createStore((set, get) => {
  const track = (key, promise) => {
    set((s) => ({ pending: { ...s.pending, [key]: true } }));
    return promise.finally(() =>
      set((s) => {
        const pending = { ...s.pending };
        delete pending[key];
        return { pending };
      }),
    );
  };
  const keep = (bucket, id, payload) => {
    set((s) => ({ [bucket]: { ...s[bucket], [id]: payload } }));
    return payload;
  };

  return {
    ...INITIAL_STATE,

    fetchCompanies: () =>
      track(
        "companies",
        call("get", URLS.COMPANIES).then((payload) => {
          set({ companies: payload });
          return payload;
        }),
      ),
    fetchQuarters: (companyId) =>
      track(
        `quarters:${companyId}`,
        call("get", URLS.QUARTERS(companyId)).then((payload) =>
          keep("quarters", companyId, payload?.quarters || []),
        ),
      ),
    fetchMembers: (companyId, quarterCompanyId) =>
      track(
        `members:${companyId}:${quarterCompanyId}`,
        call("get", URLS.MEMBERS(companyId, quarterCompanyId)).then((payload) =>
          keep("members", `${companyId}:${quarterCompanyId}`, payload),
        ),
      ),
    fetchPlan: (planId) =>
      track(
        `plan:${planId}`,
        call("get", URLS.PLAN(planId)).then((payload) => {
          // The plan carries the first change-log page.
          if (payload?.change_log)
            keep("changeLogs", planId, payload.change_log);
          return keep("plans", planId, payload);
        }),
      ),
    fetchOlderChanges: (planId) => {
      const log = get().changeLogs[planId];
      if (!log?.next_before) return Promise.resolve(log);
      return track(
        `changes:${planId}`,
        call("get", URLS.CHANGE_LOG(planId, log.next_before)).then((page) =>
          keep("changeLogs", planId, {
            items: [...(log.items || []), ...(page?.items || [])],
            next_before: page?.next_before || null,
          }),
        ),
      );
    },
    fetchReport: (planId, reportId) =>
      track(
        `report:${reportId}`,
        call("get", URLS.REPORT(planId, reportId)).then((payload) =>
          keep("reports", reportId, payload),
        ),
      ),
    startCheckIn: (reportId) =>
      track(`checkin:${reportId}`, call("post", URLS.CHECK_IN(reportId))),
    fetchChat: (planId, chatId) =>
      track(
        `chat:${chatId}`,
        call("get", URLS.CHAT_MESSAGES(planId, chatId)).then((payload) =>
          keep("chats", chatId, payload),
        ),
      ),
    fetchFinalReview: (planId) =>
      track(
        `review:${planId}`,
        call("get", URLS.FINAL_REVIEW(planId)).then((payload) =>
          keep("reviews", planId, payload),
        ),
      ),
    startFinalReview: (planId) =>
      track(
        `reviewStart:${planId}`,
        call("post", URLS.FINAL_REVIEW_START(planId)).then(async (res) => {
          await get().fetchFinalReview(planId);
          return res;
        }),
      ),
    finalise: (planId) =>
      track(
        `finalise:${planId}`,
        call("post", URLS.FINALISE(planId)).then(async (res) => {
          await Promise.all([
            get().fetchFinalReview(planId),
            get().fetchPlan(planId),
          ]);
          return res;
        }),
      ),
    remind: (planId) =>
      track(`remind:${planId}`, call("post", URLS.REMIND(planId))),
    // Final-review panel pushes (`okr_plan_final_summary`, remark saved).
    patchReview: (planId, fields) =>
      set((s) => ({
        reviews: {
          ...s.reviews,
          [planId]: { ...(s.reviews[planId] || {}), ...fields },
        },
      })),
    // Check-in pushes (`okr_plan_remark_saved`).
    patchReport: (reportId, fields) =>
      set((s) => ({
        reports: {
          ...s.reports,
          [reportId]: { ...(s.reports[reportId] || {}), ...fields },
        },
      })),

    setLastCompany: (lastCompany) => set({ lastCompany }),

    // Plan documents are view-only for the coach (CD18): download only, with
    // today's coach download route (OKR_PLANS_COACH.md §8.5 — confirm the id).
    downloadFile: (fileId, fileName) => {
      const authHeader = resolveAuthHeader();
      return nodeInstance
        .get(URLS.DOWNLOAD_FILE(fileId), {
          responseType: "blob",
          headers: authHeader ? { Authorization: authHeader } : {},
        })
        .then((response) => {
          const blob = new Blob([response.data], { type: response.data.type });
          const href = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = href;
          a.download =
            response.headers["file-name"] ||
            response.headers["content-disposition"]?.match(
              /filename="?([^"]+)"?/,
            )?.[1] ||
            fileName ||
            "download";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(href);
        });
    },

    reset: () => set(INITIAL_STATE),
  };
});

export const useOkrPlansCoachStore = (selector) => {
  const [state, setState] = useState(() =>
    selector(okrPlansCoachStore.getState()),
  );
  useEffect(() => {
    setState(selector(okrPlansCoachStore.getState()));
    return okrPlansCoachStore.subscribe((s) => {
      const next = selector(s);
      setState((prev) => (prev === next ? prev : next));
    });
  }, [selector]); // eslint-disable-line
  return state;
};

export default okrPlansCoachStore;
